import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Schema,
  SchemaPromoteRequest,
  SchemaUpdateRequest,
  SchemaEnrichmentSnapshotModel,
  apiService,
} from '../../services/api';
import { toRecord, cleanObject } from '../../utils/dataUtils';
import type { ToastState, ReviewComment } from './types';

const generateCommentId = (): string => {
  const scope =
    typeof globalThis !== 'undefined'
      ? (globalThis as typeof globalThis & { crypto?: Crypto })
      : undefined;
  if (scope?.crypto?.randomUUID) {
    try {
      return scope.crypto.randomUUID();
    } catch (_error) {
      // swallow and fallback
    }
  }
  return `comment-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export interface DraftMutationsParams {
  schemaId: string | undefined;
  draft: Schema | undefined;
  rejectReason: string;
  promoteSchemaId: string;
  promoteSchemaName: string;
  promoteEnvironment: string;
  enrichmentMetadata: Record<string, unknown>;
  schemaEnrichmentSnapshot: SchemaEnrichmentSnapshotModel | null;
  schemaEnrichmentVersion: unknown;
  commentDraft: string;
  setCommentDraft: (value: string) => void;
  setCommentSubmitting: (value: boolean) => void;
  setToast: (value: ToastState | null) => void;
  refreshSummary: () => void;
}

export function useDraftMutations(params: DraftMutationsParams) {
  const {
    schemaId,
    draft,
    rejectReason,
    promoteSchemaId,
    promoteSchemaName,
    promoteEnvironment,
    enrichmentMetadata,
    schemaEnrichmentSnapshot,
    schemaEnrichmentVersion,
    commentDraft,
    setCommentDraft,
    setCommentSubmitting,
    setToast,
    refreshSummary,
  } = params;

  const queryClient = useQueryClient();

  const promoteMutation = useMutation(
    async (payload: SchemaPromoteRequest) => {
      if (!schemaId) {
        throw new Error('Missing schema identifier.');
      }
      return apiService.promoteSchema(schemaId, payload);
    },
    {
      onSuccess: () => {
        setToast({ severity: 'success', message: 'Schema promoted successfully.' });
        queryClient.invalidateQueries(['schema', schemaId]);
        refreshSummary();
      },
      onError: (error: unknown) => {
        console.error('Draft promotion failed:', error);
        setToast({
          severity: 'error',
          message: error instanceof Error ? error.message : 'Failed to promote draft.',
        });
      },
    }
  );

  const updateDraftStatusMutation = useMutation(
    async (payload: SchemaUpdateRequest) => {
      if (!schemaId) {
        throw new Error('Missing schema identifier.');
      }
      return apiService.updateSchema(schemaId, payload);
    },
    {
      onSuccess: () => {
        setToast({ severity: 'success', message: 'Schema status updated.' });
        queryClient.invalidateQueries(['schema', schemaId]);
        refreshSummary();
      },
      onError: (error: unknown) => {
        console.error('Draft update failed:', error);
        setToast({
          severity: 'error',
          message: error instanceof Error ? error.message : 'Failed to update draft.',
        });
      },
    }
  );

  const updateDraftMetadataMutation = useMutation(
    async (payload: SchemaUpdateRequest) => {
      if (!schemaId) {
        throw new Error('Missing schema identifier.');
      }
      return apiService.updateSchema(schemaId, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['schema', schemaId]);
        refreshSummary();
      },
      onError: (error: unknown) => {
        console.error('Draft metadata update failed:', error);
        setToast({
          severity: 'error',
          message:
            error instanceof Error ? error.message : 'Failed to save enrichment metadata.',
        });
      },
    }
  );

  const handleRejectDraft = useCallback(() => {
    const metadata: Record<string, unknown> = {
      rejected_at: new Date().toISOString(),
      origin: 'web-ui',
    };
    if (rejectReason.trim()) {
      metadata.reject_reason = rejectReason.trim();
    }
    updateDraftStatusMutation.mutate({
      status: 'rejected',
      metadata,
      approvedBy: 'web-ui',
      approvedAt: new Date().toISOString(),
    });
  }, [updateDraftStatusMutation, rejectReason]);

  const handlePromoteDraft = useCallback(() => {
    if (!schemaId) {
      return;
    }

    const enrichmentForPromotion =
      Object.keys(enrichmentMetadata).length > 0 ? enrichmentMetadata : undefined;
    const snapshotForPromotion =
      schemaEnrichmentSnapshot && Object.keys(schemaEnrichmentSnapshot).length > 0
        ? schemaEnrichmentSnapshot
        : undefined;

    const metadataPayload = cleanObject({
      origin: 'web-ui',
      approved_at: new Date().toISOString(),
      enrichment: enrichmentForPromotion,
      schema_enrichment_snapshot: snapshotForPromotion,
      schema_enrichment_version: schemaEnrichmentVersion ?? undefined,
    });
    const finalMetadata =
      Object.keys(metadataPayload).length > 0 ? metadataPayload : undefined;

    promoteMutation.mutate({
      status: 'active',
      approvedBy: 'web-ui',
      metadata: finalMetadata,
    });
  }, [
    schemaId,
    promoteMutation,
    promoteSchemaId,
    promoteSchemaName,
    promoteEnvironment,
    enrichmentMetadata,
    schemaEnrichmentSnapshot,
    schemaEnrichmentVersion,
  ]);

  const handleAddComment = useCallback(async () => {
    const trimmed = commentDraft.trim();
    if (!draft || !trimmed) {
      if (!trimmed) {
        setToast({ severity: 'error', message: 'Enter a comment before posting.' });
      }
      return;
    }

    const newComment: ReviewComment = {
      comment_id: generateCommentId(),
      author: 'web-ui',
      message: trimmed,
      created_at: new Date().toISOString(),
    };

    const existingMetadata = toRecord(draft.metadata);
    const existingComments = Array.isArray(existingMetadata.review_comments)
      ? (existingMetadata.review_comments as Array<Record<string, unknown>>)
          .filter((entry) => entry && typeof entry === 'object')
          .map((entry) => ({ ...entry }))
      : [];

    const nextMetadata = cleanObject({
      ...(existingMetadata as Record<string, unknown>),
      review_comments: [
        {
          comment_id: newComment.comment_id,
          author: newComment.author,
          message: newComment.message,
          created_at: newComment.created_at,
        },
        ...existingComments,
      ],
    });

    setCommentSubmitting(true);
    try {
      await updateDraftMetadataMutation.mutateAsync({ metadata: nextMetadata });
      setCommentDraft('');
      setToast({ severity: 'success', message: 'Comment added.' });
    } catch (error) {
      console.error('Failed to add review comment', error);
      setToast({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Failed to add comment.',
      });
    } finally {
      setCommentSubmitting(false);
    }
  }, [commentDraft, draft, updateDraftMetadataMutation, setCommentDraft, setCommentSubmitting, setToast]);

  return {
    promoteMutation,
    updateDraftStatusMutation,
    updateDraftMetadataMutation,
    handleRejectDraft,
    handlePromoteDraft,
    handleAddComment,
  };
}
